import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '@/utils/api-request';
import { EmbedApiKey, EmbedKeySettings } from './types';

interface CreateEmbedKeyResponse {
  data?: EmbedApiKey;
  detail?: string;
}

interface ListEmbedKeysResponse {
  data?: { keys: EmbedApiKey[] };
  detail?: string;
}

interface RevokeEmbedKeyResponse {
  data?: { status: string };
  detail?: string;
}

export const createEmbedKey = async (
  collectionId: string,
  settings?: EmbedKeySettings,
): Promise<CreateEmbedKeyResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<EmbedApiKey>({
    url: `/${orgId}/collections/${collectionId}/external-chat/keys`,
    method: 'POST',
    target: 'platform',
    body: settings as Record<string, unknown> | undefined,
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to create embed API key',
    };
  }

  return response as CreateEmbedKeyResponse;
};

export const listEmbedKeys = async (
  collectionId: string,
): Promise<ListEmbedKeysResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<{ keys: EmbedApiKey[] }>({
    url: `/${orgId}/collections/${collectionId}/external-chat/keys`,
    method: 'GET',
    target: 'platform',
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to list embed API keys',
    };
  }

  return response as ListEmbedKeysResponse;
};

export const revokeEmbedKey = async (
  collectionId: string,
  apiKey: string,
): Promise<RevokeEmbedKeyResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<{ status: string }>({
    url: `/${orgId}/collections/${collectionId}/external-chat/keys/${apiKey}`,
    method: 'DELETE',
    target: 'platform',
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to revoke embed API key',
    };
  }

  return response as RevokeEmbedKeyResponse;
};

export const updateEmbedKeySettings = async (
  collectionId: string,
  apiKey: string,
  settings: EmbedKeySettings,
): Promise<CreateEmbedKeyResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<EmbedApiKey>({
    url: `/${orgId}/collections/${collectionId}/external-chat/keys/${apiKey}`,
    method: 'PATCH',
    body: settings as unknown as Record<string, unknown>,
    target: 'platform',
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to update embed API key settings',
    };
  }

  return response as CreateEmbedKeyResponse;
};
